(ns app.front.task
  (:require [app.domain.application.model :as app-model]
            [app.domain.bond.model :as bond-model]
            [app.domain.business.model :as business-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.review.model :as review-model]
            [app.front.dashlets :as dashlets]
            [app.front.ui :as ui]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.util :refer [for-frag] :as futil]
            [app.front.view.party.common :as party-common]
            [app.front.view.workspace.util :as ws-util]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.carbon :as cc]
            [core.front.date-util :as date-util]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.user.model :as user-model]
            [core.util.string :as str-util]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))


(def default-task-dashlet-id "dashlet-my-tasks")


(rf/reg-event-fx ::load-my-tasks
  (fn [_ [_ dashlet-id project-id]]
    {:dispatch-debounce {:id      [::load-my-tasks dashlet-id project-id]
                         :timeout 300
                         :action  :dispatch
                         :event   [::fetch-my-tasks dashlet-id project-id]}}))


(rf/reg-event-fx ::fetch-my-tasks
  (fn [_ [_ dashlet-id project-id]]
    {:comm/command {:command    :user/tasks
                    :method     :get
                    :data       (p/assoc-if {:include-workspace-summary? true}
                                            :project-id project-id)
                    :on-success [::dashlets/assoc-dashlet-data dashlet-id]}}))

(defn- nearest-workspace [entity]
  (->> (iterate :entity/parent entity)
       (take 5)
       (some #(when (:workspace/id %) %))))

(defn- workspace-summary [db dashlet-id workspace-id]
  (get-in db [::dashlets/dashlet-db dashlet-id :data :user/workspace-summary workspace-id]))

(defn- task-workspace [summary ref-entity]
  (or summary
      (some-> ref-entity :invite/application :entity/parent)
      (some-> ref-entity :application :entity/parent)
      (nearest-workspace ref-entity)
      {}))


(defn resumable-workspace-route
  "Returns the wizard resume route for draft wizard workspaces, otherwise nil. Public for tests."
  [workspace]
  (when (and (:workspace/id workspace)
             (ws-util/resume-application? workspace))
    {:route  :view/application-wizard-resume
     :params {:workspace-id (:workspace/id workspace)}}))

(defn application-task-route
  "Return the route for an application-related task. Public for tests."
  ([workspace-id task-type ref-entity sign-off?]
   (application-task-route nil workspace-id task-type ref-entity sign-off?))
  ([summary workspace-id task-type ref-entity sign-off?]
   (or (resumable-workspace-route (task-workspace summary ref-entity))
       (let [ref-type (-> ref-entity :entity/parent :entity/type)
             route    (cond
                        (#{:task.type/application} task-type)
                        {:route (case (:entity/type ref-entity)
                                  :entity.type/pre-consultation :view/workspace-pre-consultation
                                  :entity.type/application :view/workspace-application
                                  :entity.type/execution :view/workspace-execution
                                  :view/workspace)}

                        (or (= :task.type/approval task-type)
                            (review-model/review-task-type? task-type))
                        (p/assoc-if
                          {:route (case ref-type
                                    :entity.type/pre-consultation :view/workspace-pre-consultation
                                    :entity.type/execution :view/workspace-execution
                                    :view/workspace-application)}
                          :query (when (review-model/review-task-type? task-type)
                                   {:review-id (:db/id ref-entity)}))

                        (-> ref-entity :entity/type (= :entity.type/attachment-version))
                        {:route (case ref-type
                                  :entity.type/pre-consultation :view/pre-consultation-attachments
                                  :entity.type/execution :view/execution-attachments
                                  :view/application-attachments)
                         :query {:id (:db/id ref-entity)}}

                        sign-off?
                        {:route :view/sign-off}

                        (-> ref-entity :entity/type (= :entity.type/form))
                        {:route  :view/form
                         :params {:form-id (:db/id ref-entity)}}

                        :else
                        {:route :view/workspace})]
         (p/deep-merge :into route {:params {:workspace-id workspace-id}})))))


(rf/reg-event-fx ::redirect-invite-to-application
  (fn [{:keys [db]} [_ invite invite-response dashlet-id task-id]]
    (let [workspace-id (-> invite :invite/application app-model/workspace-id)]
      (merge {:dispatch [::dashlets/delete-item dashlet-id task-id]}
             (when (= invite-response :invite.status/accepted)
               {:route/push (application-task-route (workspace-summary db dashlet-id workspace-id)
                                                    workspace-id
                                                    :task.type/invite
                                                    invite
                                                    nil)})))))


(rf/reg-event-fx ::respond-to-invitation
  (fn [_ [_ invite invite-response dashlet-id task-id]]
    {:comm/command {:command    :invites/update-invitation-status
                    :data       {:invite-response invite-response
                                 :invite-id       (-> invite :db/id)}
                    :on-success [::redirect-invite-to-application
                                 invite
                                 invite-response
                                 dashlet-id
                                 task-id]}}))


(rf/reg-event-fx ::update-business-invite
  (fn [_ [_ dashlet-id task-id accept?]]
    {:comm/command {:command    :task/update-business-invite
                    :data       (util/mmap task-id accept?)
                    :on-success [::update-business-invite.success dashlet-id accept?]}}))


(rf/reg-event-fx ::update-business-invite.success
  (fn [_ [_ dashlet-id accept? status {:keys [entities removed-entities business-id]}]]
    (merge
      (when accept? {:route/push {:route :view/business :params {:business-id business-id}}})
      {:fx (concat (->> removed-entities
                        (mapv (fn [id] [:dispatch [::dashlets/delete-item dashlet-id id]])))
                   [[:dispatch [::dashlets/merge-dashlet-data dashlet-id status entities]]])})))

(rf/reg-event-fx ::update-escrow-invite
  (fn [_ [_ dashlet-id task-id accept?]]
    {:comm/command {:command    :task/claim-escrow-account
                    :data       (util/mmap task-id accept?)
                    :on-success [::update-escrow-invite.success dashlet-id accept?]}}))

(rf/reg-event-fx ::update-escrow-invite.success
  (fn [_ [_ dashlet-id accept? _ {:keys [removed-entities account-id]}]]
    (merge
      (when accept?
        {:route/push {:route  :view/escrow-account.outstanding-payments
                      :params (util/mmap account-id)}})
      {:fx (->> removed-entities
                (mapv (fn [id] [:dispatch [::dashlets/delete-item dashlet-id id]])))})))

(rf/reg-event-fx ::update-bond-invite
  (fn [_ [_ dashlet-id task-id accept?]]
    {:comm/command {:command    :task/claim-bond-account
                    :data       (util/mmap task-id accept?)
                    :on-success [::update-bond-invite.success dashlet-id accept?]}}))

(rf/reg-event-fx ::update-bond-invite.success
  (fn [_ [_ dashlet-id accept? _ {:keys [removed-entities bond-id]}]]
    (merge
      (when accept?
        {:route/push {:route  :view/bond.base
                      :params {:bond-id bond-id}}})
      {:fx (->> removed-entities
                (mapv (fn [id] [:dispatch [::dashlets/delete-item dashlet-id id]])))})))

(rf/reg-event-fx ::redirect-task-to-application
  (fn [{:keys [db]} [_ workspace-id task-type ref-entity sign-off? dashlet-id]]
    {:route/push (application-task-route (workspace-summary db dashlet-id workspace-id)
                                         workspace-id
                                         task-type
                                         ref-entity
                                         sign-off?)}))


(rf/reg-event-fx ::remove-task-from-dashlet
  (fn [_ [_ task-id dashlet-id]]
    {:dispatch [::dashlets/delete-item dashlet-id task-id]}))


(rf/reg-event-fx ::delete-task
  (fn [_ [_ task-id dashlet-id]]
    {:comm/command {:command    :user/remove-task
                    :data       {:task-id task-id}
                    :on-success [::remove-task-from-dashlet
                                 task-id
                                 dashlet-id]}}))


(rf/reg-event-fx ::fetch-assignees
  (fn [_ [_ group-id dashlet-id]]
    {:comm/command {:command    :group/assignees
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::dashlets/merge-dashlet-data dashlet-id]}}))


(rf/reg-event-fx ::assign-task
  (fn [_ [_ task-id user-id dashlet-id]]
    {:comm/command {:command    :task/assign
                    :method     :post
                    :data       {:task-id        task-id
                                 :target-user-id user-id}
                    :on-success [::dashlets/merge-dashlet-data dashlet-id]}}))


(rf/reg-sub ::workspace-summary
  (fn [[_ dashlet-id] _]
    (rf/subscribe [::dashlets/dashlet dashlet-id]))
  (fn [dashlet _]
    (get-in dashlet [:data :user/workspace-summary])))


(defn- review-task-urgent?
  "Returns true if a review task has a due date within specified number of days from now or is overdue."
  [task]
  (boolean
    (and (review-model/review-task-type? (:task/type task))
         (some-> task :task/due (date-util/before? (date-util/add-days-to-date (js/Date.) review-model/reminder-due-days))))))

(rf/reg-sub ::sorted-task-items
  (fn [[_ dashlet-id] _]
    (rf/subscribe [::dashlets/dashlet dashlet-id]))
  (fn [dashlet _]
    (let [all                        (->> dashlet :data vals
                                          (filter :entity/created)
                                          (filter (eu/is-type-fn :entity.type/task :entity.type/order-acknowledgement)))
          {verification-tasks true
           other-tasks        false} (group-by #(= :task.type/property-verification (:task/type %)) all)
          {urgent-reviews true
           normal-tasks   false}     (group-by review-task-urgent? other-tasks)]
      (concat (sort-by :entity/created > urgent-reviews)
              (sort-by :entity/created > normal-tasks)
              (sort-by :entity/created > verification-tasks)))))


(defn common-invite-actions [{:keys [tr accept-evt decline-evt]}]
  (r/with-let [*responded? (r/atom false)]
    (if @*responded?
      [:div (cc/button-skeleton)]
      [:div.flex.flex-col.xl:flex-row
       (cc/button
         {:kind       :tertiary
          :data-cy    "decline-invitation"
          :class-name "mr-1 my-1"
          :on-click   #(do (reset! *responded? true)
                           (>evt decline-evt))}
         (tr [:invite/invitation-decline]))
       (cc/button
         {:kind       :primary
          :data-cy    "accept-invitation"
          :class-name "mr-1 mb-1 xl:mt-1"
          :on-click   #(do (reset! *responded? true)
                           (>evt accept-evt))}
         (tr [:invite/invitation-accept]))])))


(defn invite-actions [{:keys [tr my-id]} task invite]
  [common-invite-actions
   (util/mmap {:accept-evt  [::respond-to-invitation invite :invite.status/accepted my-id (:db/id task)]
               :decline-evt [::respond-to-invitation invite :invite.status/declined my-id (:db/id task)]}
              tr)])


(defn join-business-actions [{:keys [tr my-id task]}]
  [common-invite-actions (util/mmap {:accept-evt  [::update-business-invite my-id (:db/id task) true]
                                     :decline-evt [::update-business-invite my-id (:db/id task) false]}
                                    tr)])


(defn claim-escrow-account-actions [{:keys [tr my-id task]}]
  [common-invite-actions (util/mmap {:accept-evt  [::update-escrow-invite my-id (:db/id task) true]
                                     :decline-evt [::update-escrow-invite my-id (:db/id task) false]}
                                    tr)])


(defn claim-bond-account-actions [{:keys [tr my-id task]}]
  [common-invite-actions (util/mmap {:accept-evt  [::update-bond-invite my-id (:db/id task) true]
                                     :decline-evt [::update-bond-invite my-id (:db/id task) false]}
                                    tr)])


(defn assign-button [{:keys [tr my-id]} task]
  (r/with-let [*assign-visible? (r/atom false)
               *evt-to-confirm  (r/atom nil)]
    (if @*assign-visible?
      (let [group-id     (-> task :task/user-group :db/id)
            _            (>evt [::fetch-assignees group-id my-id])
            current-user (<sub [:session/user])
            avail-users  (->> (<sub [::dashlets/dashlet-entities my-id (filter #(some (fn [g]
                                                                                        (= group-id g))
                                                                                      (:user/groups %)))])
                              (sort #(user-model/user-sort (:db/id current-user) %1 %2)))
            user-id      (or (and (-> task :task/assignees first :entity/type (= :entity.type/user))
                                  (-> task :task/assignees first :db/id))
                             :none)]
        (if (seq avail-users)
          [:div.flex.items-center
           (cc/select
             {:id            (str (:db/id task) "-re-assign")
              :class-name    "full-width-select"
              :light         true
              :label-text    (tr [:approval/assignee])
              :hide-label    true
              :default-value user-id
              :inline        true
              :on-change     (fn [e]
                               (reset! *evt-to-confirm [::assign-task (:db/id task) (-> e futil/tval js/parseInt) my-id]))}
             (when (= user-id :none)
               [ui/placeholder-select-item tr :none])

             (when-not (contains? (set (map :db/id avail-users)) (:db/id current-user))
               (cc/select-item {:text  (user-model/full-name current-user)
                                :value (:db/id current-user)}))

             (for-frag [user avail-users]
               (cc/select-item {:text  (user-model/full-name user)
                                :value (:db/id user)})))
           (when @*evt-to-confirm
             [:<>
              (cc/button
                {:kind             :ghost
                 :size             :small
                 :class-name       "only-icon-button"
                 :icon             i/remove
                 :icon-description (tr [:cancel])
                 :icon-position    :right
                 :on-click         (fn [_]
                                     (reset! *assign-visible? false)
                                     (reset! *evt-to-confirm nil))}
                "")
              (cc/button
                {:kind             :ghost
                 :size             :small
                 :class-name       "only-icon-button"
                 :icon             i/check
                 :icon-description (tr [:confirm])
                 :icon-position    :right
                 :on-click         (fn [_]
                                     (>evt @*evt-to-confirm)
                                     (reset! *assign-visible? false)
                                     (reset! *evt-to-confirm nil))}
                "")])]
          (cc/skeleton-text)))
      (cc/button
        {:kind     :tertiary
         :size     :small
         :on-click #(reset! *assign-visible? true)}
        (or (-> task :task/assignees first user-model/full-name str-util/blank->nil)
            (tr [:task/no-assignee]))))))

(rf/reg-event-fx
  ::navigate-to-permit-sign-off
  (fn [_ [_ workspace-id]]
    {:set-location [:view/sign-permits {:workspace-id workspace-id}]}))

(rf/reg-event-fx ::navigate-to-bond-view
  (fn [_ [_ bond-id]]
    {:route/push {:route  :view/bond.base
                  :params {:bond-id bond-id}}}))

(rf/reg-event-fx ::navigate-to-property-dashboard
  (fn [_]
    {:route/push {:route :view/property-dashboard}}))

(def remove-means-acknowledge?
  (comp #{:task.type/application}
        :task/type))

(defn task-actions [{:keys [tr my-id show-button-labels?]} task ref-entity & [workspace-id]]
  (let [sign-off-form-id (-> ref-entity :application :application/sign-off-form :db/id)
        ref-parent-id    (-> ref-entity :entity/parent :db/id)]
    [:div.flex.flex-0.flex-row.self-stretch
     (when (:task/removable? task)
       (let [title (tr [(if (remove-means-acknowledge? task)
                          :task/mark-as-seen
                          :task/remove)])]
         (cc/button
           {:kind             :ghost
            :icon             (if (remove-means-acknowledge? task)
                                i/check
                                i/remove)
            :icon-description title
            :icon-position    :right
            :class-name       "only-icon-button"
            :on-click         #(>evt [::delete-task (:db/id task) my-id])}
           (if show-button-labels? title ""))))
     (cc/button
       {:icon             i/chevron-right
        :icon-description (tr [:task/go-to])
        :class-name       "only-icon-button self-stretch"
        :icon-position    :right
        :kind             :primary
        :on-click         #(>evt (case (:task/type task)
                                   :task.type/permit-sign-off
                                   [::navigate-to-permit-sign-off (-> ref-entity :entity/parent :entity/parent :workspace/id)]

                                   :task.type/bond-verification
                                   [::navigate-to-bond-view (:bond/id ref-entity)]

                                   :task.type/claim-bond-account
                                   [::navigate-to-bond-view (:bond/id ref-entity)]

                                   :task.type/property-verification
                                   [::navigate-to-property-dashboard]

                                   [::redirect-task-to-application
                                    (if (-> task :task/type (= :task.type/comment))
                                      (-> ref-entity :entity/parent :entity/parent app-model/workspace-id)
                                      (or workspace-id
                                          (-> ref-entity :entity/parent app-model/workspace-id)
                                          (-> ref-entity :application app-model/workspace-id)
                                          (-> ref-entity :entity/parent :workspace/id)))
                                    (:task/type task)
                                    (if (-> task :task/type (= :task.type/acceptance))
                                      (-> ref-entity :entity/parent)
                                      ref-entity)
                                    (and (= sign-off-form-id ref-parent-id)
                                         sign-off-form-id
                                         ref-parent-id)
                                    my-id]))}
       (if show-button-labels? (tr [:task/go-to]) ""))]))

(defn acknowledgement-row [tr acknowledgement]
  (let [workspace (-> acknowledgement :order.acknowledgement/workspace)]
    [:div.flex.flex-1.items-center.justify-between.bg-ui-light-gray.pl-4.mt-2.border-solid.border-l-4.border-ui-red-100
     [:div.w-8.hidden.md:block]
     [:div.flex.flex-1.flex-col.md:flex-row.flex-wrap.pb-3
      [:div.flex.flex-row.items-center.pt-3.mr-3.w-48
       [:div.flex.flex-col
        [:div.font-bold.mb-2 (tr [:order/inspection-order])]
        [:div ((<sub [::date-util/date-time-fn]) (-> acknowledgement :entity/created))]]]
      [:div.flex.flex-col.pt-3.mr-3.dashboard__task-row__description
       [:div.font-bold.mb-2
        (:workspace/address workspace)]
       [:span (tr [:order/order-assigned])]]
      [:div.flex.flex-col.pt-3.mr-3
       [:div.font-medium.mb-2 (tr [:task/requested-by])]
       [:div (user-model/full-name (:entity/creator acknowledgement))]]]

     [task-actions {:tr tr} acknowledgement acknowledgement (:workspace/id workspace)]]))

(defn task-title [{:task/keys [type]} ref-entity]
  (let [tr   (<sub [:tr])
        lang (<sub [:language])]
    [:div.font-bold.mb-2
     (if (= type :task.type/application)
       (or (-> ref-entity :entity/parent (config-model/category-name lang))
           (-> ref-entity :entity/parent :workspace/permit-type vector tr))
       (tr [type]))]))

(defmulti task->address (fn [task _ref-entity] (:task/type task)))

(defmethod task->address
  :task.type/attachment-requirements
  [_ ref-entity]
  (-> ref-entity
      :entity/parent
      :workspace/address))

(defmethod task->address
  :task.type/party-role-requirements
  [_ requirement]
  (-> requirement
      :entity/parent
      :entity/parent
      :workspace/address))

(defmethod task->address
  :task.type/comment
  [_ ref-entity]
  (-> ref-entity
      :entity/parent ; topic
      :entity/parent ; phase
      :entity/parent ; ws
      :workspace/address))

(defmethod task->address
  :default
  [_ ref-entity]
  (let [application (or (-> ref-entity :invite/application)
                        (-> ref-entity :application)
                        (-> ref-entity :entity/parent))]
    (-> (:entity/parent application)
        :workspace/address)))

(defmulti task->requested-by (fn [task _ref-entity] (:task/type task)))

(defmethod task->requested-by
  :task.type/attachment-requirements
  [_ _]
  nil)

(defmethod task->requested-by
  :task.type/bond-verification
  [_ _]
  nil)

(defmethod task->requested-by
  :task.type/party-role-requirements
  [task _]
  (:entity/creator task))

(defmethod task->requested-by
  :default
  [task ref-entity]
  (or (:entity/creator ref-entity)
      (:invite/inviter ref-entity)
      (:entity/creator task)))

(defn task-row [{:keys [tr] :as conf} task ref-entity]
  (r/with-let [*expanded? (r/atom false)]
    (let [border-col-class (case (:task/type task)
                             :task.type/due-payment "border-ui-yellow"
                             (:task.type/acceptance :task.type/property-verification) "border-ui-red-100"
                             "")
          lang             (<sub [:language])]
      [:div.flex.flex-1.items-center.justify-between.bg-ui-light-gray.pl-3.mt-2.border-solid.border-l-4
       {:key     (str (:db/id task) "-r1")
        :data-cy "task-tile"
        :class   border-col-class}
       [:div.flex-shrink-0.hidden.md:block.md:pr-3
        ; TODO: Do the other task types show something in expanded view?
        (when (and (-> task :task/description str-util/blank->nil)
                   (-> task :task/type #{:task.type/property-verification} not))
          [:button.bx--table-expand__button
           (cc/icon {:icon        (if @*expanded?
                                    i/chevron-up
                                    i/chevron-down)
                     :description (if @*expanded?
                                    (tr [:carbon.table.row.collapse])
                                    (tr [:carbon.table.row.expand]))
                     :on-click    #(swap! *expanded? not)})])]
       [:div.flex.flex-col.w-full
        [:div.flex.flex-1.flex-col.md:flex-row.flex-wrap.pb-3.md:items-center
         [:div.flex.flex-row.items-center.pt-3.mr-3.w-48
          [:div.flex.flex-col
           [task-title task ref-entity]
           [:div ((<sub [::date-util/date-time-fn]) (-> task :entity/created))]]
          (when (some-> task :task/due date-util/before-now?)
            [:div.ml-2
             [ui/tag {:color :yellow} (tr [:overdue])]])]

         [:div.flex.flex-col.pt-3.mr-3.dashboard__task-row__description
          (when-let [address (task->address task ref-entity)]
            [:div.font-bold.mb-2
             address])
          (if-let [due (:task/due task)]
            [:div
             (tr [:task/due]) " " ((<sub [::date-util/date-fn]) due)]
            [:div
             (let [task-type (:task/type task)]
               (cond
                 (= :task.type/invite task-type)
                 [:span
                  (if-let [business-name (-> ref-entity :invite/group :group/name)]
                    (tr [:invite/invitation-leading-text-business]
                        [business-name])
                    (tr [:invite/invitation-leading-text]))
                  (->> ref-entity
                       :invite/party
                       :party/party-roles
                       (map party-common/party-role-label)
                       (str/join ", "))]

                 (review-model/review-task-type? task-type) (review-model/review-name ref-entity lang)
                 (= :task.type/due-payment task-type) (tr [(:bill/phase ref-entity)])
                 (= :task.type/acceptance task-type) (str (-> ref-entity :acceptance/value vector tr)
                                                          ": "
                                                          (-> ref-entity :entity/parent :entity/type vector tr))
                 (= :task.type/application task-type) (or (-> ref-entity :entity/parent :workspace/municipal-case-id)
                                                          (-> ref-entity :entity/parent :workspace/id))
                 (= :task.type/join-business task-type) (-> ref-entity (business-model/format-name lang))
                 (= :task.type/bond-verification task-type) [:div
                                                             [:div.font-medium.mb-2 (tr [:bond/applicant])]
                                                             [:div (bond-model/bond-owner-name lang (:bond/owner ref-entity))]]
                 (= :task.type/property-verification task-type)
                 (tr [:property/verification-task-count] [(:task/description task)])

                 :else ""))])]

         (when-let [requested-by (task->requested-by task ref-entity)]
           [:div.flex.flex-col.pt-3.mr-6
            [:div.font-medium.mb-2 (tr [:task/requested-by])]
            [:div (-> requested-by
                      user-model/full-name)]])

         (when (:task/assignable? task)
           [:div.flex.flex-col.pt-3.mr-3
            [:div.font-medium.mb-2 (tr [:assignee/assignee])]
            [:div [assign-button conf task]]])]

        (when @*expanded?
          [:div.flex.flex-1.border-t.border-solid.border-ui-gray.pt-3.mr-4
           {:key (str (:db/id task) "-r2")}
           [:div.pb-3
            [:div.font-bold (tr [:task/description])]
            [:div.mt-2 (or (when-let [desc (-> task :task/description str-util/blank->nil)]
                             [ui-rt/editor-legacy {:initial-value desc
                                                   :id            (str (:db/id task) "-description-display")}])
                           [:span.italic (tr [:task/no-description])])]]])]


       (case (:task/type task)
         :task.type/invite [invite-actions conf task ref-entity]
         :task.type/join-business [join-business-actions (assoc conf :task task)]
         :task.type/claim-escrow-account [claim-escrow-account-actions (assoc conf :task task)]
         :task.type/claim-bond-account [claim-bond-account-actions (assoc conf :task task)]
         [task-actions conf task ref-entity])])))

(defn dashlet-content [{:keys [tr id task-items]}]
  [:<>
   (for-frag [item task-items]
     (if (eu/is-type? :entity.type/task item)
       ^{:key (:db/id item)}
       [task-row {:tr tr :my-id id} item (<sub [::dashlets/dashlet-entity id (-> item :task/reference)])]
       ^{:key (:db/id item)}
       [acknowledgement-row tr (<sub [::dashlets/dashlet-entity id (:db/id item)])]))])

(defn task-dashlet [{:keys [id heading-i18n-key]}]
  (let [tr         (<sub [:tr])
        task-items (<sub [::sorted-task-items id])]
    (if-not (<sub [::dashlets/dashlet-loading? id])
      (when (seq task-items)
        [:section.dashlet
         [:h2 (tr [(or heading-i18n-key :invite/invitations-header)])]
         [dashlet-content (util/mmap tr id task-items)]])
      (cc/skeleton-text {}))))
